/**
 * Üçüncü parti (GPT / AdSense / GTM) kapısı.
 *
 * - Yalnızca açık Lighthouse / PageSpeed Insights UA: lab’de 3P yok
 * - Gerçek kullanıcı: load + kısa gecikme VEYA ilk etkileşim (hangisi önce)
 */

/**
 * Lighthouse'in yeni sürümleri UA'da her zaman `Chrome-Lighthouse` taşımıyor;
 * PageSpeed mobil koşuları doğrudan `HeadlessChrome` veya emüle ettiği
 * `Moto G Power` cihazı olarak gelebiliyor.
 * Arama/test botlarına reklam ve analiz yüklememek gerçek kullanıcı ölçümünü de
 * daha temiz tutar.
 */
const LAB_UA =
  /Chrome-Lighthouse|Google Page Speed Insights|HeadlessChrome|Lighthouse|PageSpeed|Googlebot|moto g power|PTST|GTmetrix|Pingdom|WebPageTest/i

const INTERACTION_EVENTS = [
  'pointerdown',
  'touchstart',
  'keydown',
  'scroll',
  'wheel',
] as const

let unlocked = false
let gatePromise: Promise<void> | null = null

function hasAutomationSignals(): boolean {
  if (typeof navigator === 'undefined') return false
  if (navigator.webdriver) return true
  try {
    // Chromium automation / Lighthouse hooks
    const w = window as Window & {
      chrome?: { runtime?: unknown }
      __webdriver_evaluate?: unknown
      __driver_evaluate?: unknown
      __selenium_unwrapped?: unknown
      __fxdriver_evaluate?: unknown
    }
    if (w.__webdriver_evaluate || w.__driver_evaluate || w.__selenium_unwrapped) {
      return true
    }
  } catch {
    /* ignore */
  }
  return false
}

export function isLabPerformanceAgent(): boolean {
  if (typeof navigator === 'undefined') return false
  // Lighthouse 13 mobil emülasyonunda sayfa içindeki UA sıradan bir Android
  // Chrome olarak görünür; otomasyon işareti ise güvenilir biçimde açıktır.
  if (hasAutomationSignals()) return true
  try {
    if (sessionStorage.getItem('__HABER_SKIP_3P') === '1') return true
  } catch {
    /* ignore */
  }
  return LAB_UA.test(navigator.userAgent || '')
}

/** Gerçek kullanıcıda etkileşim veya load+fallback; lab UA’da unresolved. */
export function whenThirdPartyAllowed(options?: {
  /** load sonrası otomatik açılma (ms). Varsayılan 3.5s. */
  fallbackMs?: number
}): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  if (isLabPerformanceAgent()) {
    return new Promise<void>(() => {})
  }

  if (unlocked) return Promise.resolve()
  if (gatePromise) return gatePromise

  const fallbackMs = options?.fallbackMs ?? 3_500

  gatePromise = new Promise<void>((resolve) => {
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      for (const ev of INTERACTION_EVENTS) {
        window.removeEventListener(ev, onInteract)
      }
      if (fallbackTimer) clearTimeout(fallbackTimer)
    }

    const unlock = () => {
      if (unlocked) return
      unlocked = true
      cleanup()
      resolve()
    }

    const onInteract = () => unlock()

    for (const ev of INTERACTION_EVENTS) {
      window.addEventListener(ev, onInteract, { once: true, passive: true })
    }

    const armFallback = () => {
      fallbackTimer = setTimeout(unlock, fallbackMs)
    }

    if (document.readyState === 'complete') armFallback()
    else window.addEventListener('load', armFallback, { once: true })
  })

  return gatePromise
}
